/**
 * Engine predicates the toolbar uses twice over: as the `engineFilter` passed
 * to `requestContext` at click time, and as the visibility/disabled gate for
 * the matching item. Sharing one predicate for both is what makes it
 * impossible for a visible item to resolve a connection its own action would
 * reject (INJ-2 round 2).
 *
 * Moved out of `MainToolbar.tsx` by task B11; still re-exported from there so
 * existing importers (and `MainToolbar.gating.test.ts`) keep working.
 */
import {
  engineSupports,
  getEngineDisplayInfo,
  isValidEngineId,
  type DatabaseEngineId,
} from '@/core/engines';

/**
 * New Query / Query Editor — excludes engines with no query language at all
 * (Azure Storage). Capability-driven, never `engine === 'azurestorage'`: any
 * engine whose definition sets `workspaceFeatures.supportsQueryEditor: false`
 * is gated by the same predicate (S1 Task 1.16, spec §6.2).
 *
 * `undefined` is a legacy connection saved before the engine field existed and
 * is treated as mssql, exactly like the notebook / schema-overview filters
 * below; an unrecognised string is refused rather than assumed supported.
 */
export function supportsQueryEditorEngineFilter(engine: string | undefined): boolean {
  return engineSupports(resolveEngineId(engine), 'workspaceFeatures', 'supportsQueryEditor');
}

/** Table Explorer — same contract, for `workspaceFeatures.supportsTableExplorer`. */
export function supportsTableExplorerEngineFilter(engine: string | undefined): boolean {
  return engineSupports(resolveEngineId(engine), 'workspaceFeatures', 'supportsTableExplorer');
}

/**
 * Table Designer — engines that can create a table (`scripting.supportsCreateTable`: false for Kusto and Azure Storage).
 * UI review F4: the home card gated Designer on the connection alone, so a Storage connection opened "Select Database".
 */
export function supportsTableDesignerEngineFilter(engine: string | undefined): boolean {
  return engineSupports(resolveEngineId(engine), 'scripting', 'supportsCreateTable');
}

/** Why-tooltip for a disabled Table Designer entry point. */
export function tableDesignerUnsupportedReason(engine: string | undefined): string {
  return `Table Designer isn't available for ${engineLabel(engine)} connections`;
}

/** Dashboards — a dashboard tile is a query, so an engine without a query language has none (Azure Storage). */
export function supportsDashboardsEngineFilter(engine: string | undefined): boolean {
  return supportsQueryEditorEngineFilter(engine);
}

/** Why-tooltip for a disabled Dashboards entry point. */
export function dashboardsUnsupportedReason(engine: string | undefined): string {
  return `Dashboards need a query language; ${engineLabel(engine)} connections have none`;
}

/** Why-tooltip for a disabled Query Editor entry point. */
export function queryEditorUnsupportedReason(engine: string | undefined): string {
  return `${engineLabel(engine)} connections have no query language`;
}

/** Why-tooltip for a disabled Table Explorer entry point. */
export function tableExplorerUnsupportedReason(engine: string | undefined): string {
  return `Table Explorer isn't available for ${engineLabel(engine)} connections`;
}

/** An unknown string resolves to an id no engine owns, so `engineSupports` says no. */
function resolveEngineId(engine: string | undefined): DatabaseEngineId {
  if (engine === undefined) return 'mssql';
  return isValidEngineId(engine) ? engine : ('unknown-engine' as DatabaseEngineId);
}

function engineLabel(engine: string | undefined): string {
  if (engine === undefined || !isValidEngineId(engine)) return 'These';
  return getEngineDisplayInfo(engine)?.displayName ?? engine;
}

/** New Notebook — excludes engines without notebook session support. */
export function supportsNotebooksEngineFilter(engine: string | undefined): boolean {
  return engineSupports((engine ?? 'mssql') as DatabaseEngineId, 'workspaceFeatures', 'supportsNotebooks');
}

/** Schema Overview — excludes engines without Schema Overview support (kusto). */
export function supportsSchemaOverviewEngineFilter(engine: string | undefined): boolean {
  return engineSupports((engine ?? 'mssql') as DatabaseEngineId, 'workspaceFeatures', 'supportsSchemaOverview');
}

/** Spatial Explorer — excludes engines without spatial types (kusto). */
export function supportsSpatialExplorerEngineFilter(engine: string | undefined): boolean {
  return engineSupports((engine ?? 'mssql') as DatabaseEngineId, 'spatial', 'supportsSpatialTypes');
}

/** Tablespace Manager / Package Explorer / Data Pump — Oracle only. */
export function supportsOracleDbaEngineFilter(engine: string | undefined): boolean {
  return engine === 'oracle';
}

/**
 * Filegroup Manager — MSSQL only. `undefined` is treated as mssql (legacy
 * connections predating the engine field), unlike the Oracle filter above,
 * which never assumes an unlabeled connection is Oracle.
 */
export function supportsMssqlDbaEngineFilter(engine: string | undefined): boolean {
  return engine === 'mssql' || engine === undefined;
}
